/**
 * Product-offer discount helpers (editor, publish, preview, discount function).
 */

export const DISCOUNT_SCOPE = Object.freeze({
  OVERALL: "overall",
  PER_PRODUCT: "per_product",
});

/** Legacy UI values still present on saved popups. */
const DISCOUNT_SCOPE_ALIASES = Object.freeze({
  all: DISCOUNT_SCOPE.OVERALL,
  specific: DISCOUNT_SCOPE.PER_PRODUCT,
  overall: DISCOUNT_SCOPE.OVERALL,
  per_product: DISCOUNT_SCOPE.PER_PRODUCT,
});

export const DISCOUNT_SCOPE_OPTIONS = Object.freeze([
  { label: "Overall product", value: DISCOUNT_SCOPE.OVERALL },
  { label: "Per product", value: DISCOUNT_SCOPE.PER_PRODUCT },
]);

/** Metafield rule productId when discount applies to any product from the popup offer. */
export const OFFER_DISCOUNT_WILDCARD_PRODUCT_ID = "*";

/** Discount UI / metafield source when using Default offer products_to_offer. */
export const DEFAULT_OFFER_DISCOUNT_SOURCE_ID = "default";

export function offerRuleDiscountSourceId(ruleId) {
  if (ruleId == null || ruleId === "") return "";
  return `rule:${String(ruleId)}`;
}

/**
 * @param {string|null|undefined} sourceId
 * @returns {{ type: "default"|"rule", ruleId: string|null }}
 */
export function parseOfferDiscountSourceId(sourceId) {
  const raw = sourceId != null ? String(sourceId) : "";
  if (!raw || raw === DEFAULT_OFFER_DISCOUNT_SOURCE_ID) {
    return { type: "default", ruleId: null };
  }
  if (raw.startsWith("rule:")) {
    const ruleId = raw.slice(5);
    return { type: "rule", ruleId: ruleId || null };
  }
  return { type: "default", ruleId: null };
}

/** Metafield / cart attribute value for a discount source (`default` or rule id). */
export function toMetafieldOfferRuleId(sourceId) {
  const parsed = parseOfferDiscountSourceId(sourceId);
  if (parsed.type === "rule" && parsed.ruleId) return parsed.ruleId;
  return DEFAULT_OFFER_DISCOUNT_SOURCE_ID;
}

/**
 * Collect specific-product discount sources (Default + each Offer Rule).
 * @param {object} productCond
 * @param {object} [options]
 * @param {object} [options.productSection] — Prefer section.products for Default when present
 * @param {boolean} [options.includeEmpty=false]
 * @returns {Array<{ id: string, label: string, offerRuleId: string, products: object[] }>}
 */
export function collectSpecificOfferDiscountSources(
  productCond,
  options = {}
) {
  const sources = [];
  const productSection = options.productSection || null;
  const includeEmpty = options.includeEmpty === true;
  const defaultEnabled = productCond?.defaultOfferEnabled !== false;
  const defaultOffer = productCond?.products_to_offer;
  const isSpecific = (offer) =>
    offer?.type === "specific_product" && Array.isArray(offer?.value);

  if (defaultEnabled && isSpecific(defaultOffer)) {
    const fromSection = Array.isArray(productSection?.products)
      ? productSection.products.filter((p) => p?.id)
      : [];
    const fromOffer = defaultOffer.value.filter((p) => p?.id);
    const offerById = buildOfferDiscountConfigByProductId(fromOffer);
    const products = fromSection.length
      ? fromSection.map((product) => {
          const offerEntry = offerById.get(normalizeProductId(product.id));
          if (!offerEntry) return product;
          return {
            ...offerEntry,
            ...product,
            ...pickOfferProductDiscountFields(offerEntry),
            ...pickOfferProductDiscountFields(product),
          };
        })
      : fromOffer;
    if (includeEmpty || products.length) {
      sources.push({
        id: DEFAULT_OFFER_DISCOUNT_SOURCE_ID,
        label: "Default offer",
        offerRuleId: DEFAULT_OFFER_DISCOUNT_SOURCE_ID,
        products,
      });
    }
  }

  const rules = Array.isArray(productCond?.offerRules)
    ? productCond.offerRules
    : [];
  rules.forEach((rule, index) => {
    if (!rule || rule.enabled === false) return;
    if (!isSpecific(rule.products_to_offer)) return;
    const products = rule.products_to_offer.value.filter((p) => p?.id);
    if (!includeEmpty && !products.length) return;
    const ruleId =
      rule.id != null ? String(rule.id) : `rule_${index}`;
    sources.push({
      id: offerRuleDiscountSourceId(ruleId),
      label: `Rule ${index + 1}`,
      offerRuleId: ruleId,
      products,
    });
  });

  return sources;
}

export function mapProductsToDiscountRows(
  products,
  sectionDiscount = {}
) {
  return (Array.isArray(products) ? products : []).map((p) => ({
    id: p.id,
    title: p.title,
    type: p.discount_type || sectionDiscount.type || "percentage",
    value: Number(p.discount_value) || 0,
    label: p.discount_message || sectionDiscount.label || "",
    limitEnabled: Boolean(p.discount_quantity_limit_enabled),
    limitQty: Number(p.discount_quantity_limit) || 1,
  }));
}

/**
 * Apply discount patch fields onto a product-like object.
 */
export function applyDiscountPatchToProduct(product, discountPatch) {
  if (!product || typeof product !== "object") return product;
  const d = discountPatch || {};
  const next = { ...product };
  if (d.type != null) next.discount_type = d.type;
  if (d.value != null) next.discount_value = d.value;
  if (d.label != null) next.discount_message = d.label;
  if (typeof d.limitEnabled === "boolean") {
    next.discount_quantity_limit_enabled = d.limitEnabled;
  }
  if (d.limitQty != null) next.discount_quantity_limit = d.limitQty;
  return next;
}

export function hasActiveDiscountValue(discountValue) {
  const numeric = Number(discountValue);
  return Number.isFinite(numeric) && numeric > 0;
}

export function hasActiveProductDiscount(product) {
  return hasActiveDiscountValue(product?.discount_value);
}

export function hasActiveSectionDiscount(section) {
  return hasActiveDiscountValue(section?.discount_value);
}

export function normalizeProductId(value) {
  if (value == null) return "";
  const raw = String(value);
  if (raw.includes("/")) {
    return raw.split("/").pop() || "";
  }
  return raw;
}

/**
 * Canonicalize discount_scope from UI aliases ("all"/"specific") and runtime values.
 */
export function normalizeDiscountScope(scope) {
  if (scope == null || scope === "") return null;
  return DISCOUNT_SCOPE_ALIASES[String(scope)] || null;
}

export function getDiscountScope(section) {
  const normalized = normalizeDiscountScope(section?.discount_scope);
  return normalized || DISCOUNT_SCOPE.PER_PRODUCT;
}

export function getDiscountQuantityLimit(source) {
  if (!source?.discount_quantity_limit_enabled) return null;
  const limit = Number(source?.discount_quantity_limit);
  return Number.isFinite(limit) && limit > 0 ? limit : null;
}

export function pickOfferProductDiscountFields(product) {
  if (!product || typeof product !== "object") return {};
  const fields = {};
  if (product.discount_type != null) fields.discount_type = product.discount_type;
  if (product.discount_value != null) fields.discount_value = product.discount_value;
  if (product.discount_message != null) {
    fields.discount_message = product.discount_message;
  }
  if (product.discount_quantity_limit_enabled != null) {
    fields.discount_quantity_limit_enabled =
      product.discount_quantity_limit_enabled;
  }
  if (product.discount_quantity_limit != null) {
    fields.discount_quantity_limit = product.discount_quantity_limit;
  }
  return fields;
}

/**
 * Build id → configured product map. Later lists win (section.products should be last).
 */
export function buildOfferDiscountConfigByProductId(...productLists) {
  const map = new Map();
  for (const list of productLists) {
    if (!Array.isArray(list)) continue;
    for (const product of list) {
      const id = normalizeProductId(product?.id);
      if (!id) continue;
      map.set(id, product);
    }
  }
  return map;
}

function discountFieldsFromSource(source) {
  return {
    discount_value: source.discount_value,
    discount_type: source.discount_type || "percentage",
    discount_message: source.discount_message || "",
    discount_quantity_limit_enabled: source.discount_quantity_limit_enabled,
    discount_quantity_limit: source.discount_quantity_limit,
  };
}

/**
 * Resolve discount fields for one offered product from section + product config.
 * Scope is strict:
 * - overall → only section discount (never leftover per-product rows)
 * - per_product → only that product's discount (0 means none; never fall back to section)
 * @returns {{ discount_value?: *, discount_type?: string, discount_message?: string, discount_quantity_limit_enabled?: boolean, discount_quantity_limit?: number }}
 */
export function resolveOfferProductDiscount(
  section,
  product,
  configByIdOrProducts
) {
  const scope = getDiscountScope(section);

  if (scope === DISCOUNT_SCOPE.OVERALL) {
    if (hasActiveSectionDiscount(section)) {
      return discountFieldsFromSource(section);
    }
    return {};
  }

  let configured = null;
  let foundConfiguredEntry = false;
  if (configByIdOrProducts instanceof Map) {
    const id = normalizeProductId(product?.id);
    if (id && configByIdOrProducts.has(id)) {
      foundConfiguredEntry = true;
      configured = configByIdOrProducts.get(id) || null;
    }
  } else if (Array.isArray(configByIdOrProducts)) {
    const map = buildOfferDiscountConfigByProductId(configByIdOrProducts);
    const id = normalizeProductId(product?.id);
    if (id && map.has(id)) {
      foundConfiguredEntry = true;
      configured = map.get(id) || null;
    }
  } else if (Array.isArray(section?.products) && section.products.length) {
    // Prefer schema/config row over stale discount fields already on the product.
    const map = buildOfferDiscountConfigByProductId(section.products);
    const id = normalizeProductId(product?.id);
    if (id && map.has(id)) {
      foundConfiguredEntry = true;
      configured = map.get(id) || null;
    } else if (
      product &&
      Object.prototype.hasOwnProperty.call(product, "discount_value")
    ) {
      foundConfiguredEntry = true;
      configured = product;
    }
  } else if (
    product &&
    Object.prototype.hasOwnProperty.call(product, "discount_value")
  ) {
    foundConfiguredEntry = true;
    configured = product;
  }

  if (foundConfiguredEntry) {
    if (configured && hasActiveProductDiscount(configured)) {
      return discountFieldsFromSource(configured);
    }
    // Explicit 0 / empty per-product discount — do not use section overall leftover.
    return {};
  }

  return {};
}

function stripProductDiscountFields(product) {
  if (!product || typeof product !== "object") return product;
  const next = { ...product };
  delete next.discount_value;
  delete next.discount_type;
  delete next.discount_message;
  delete next.discount_quantity_limit_enabled;
  delete next.discount_quantity_limit;
  return next;
}

export function mergeDiscountOntoProduct(section, product, configByIdOrProducts) {
  if (!product || typeof product !== "object") return product;
  const discount = resolveOfferProductDiscount(
    section,
    product,
    configByIdOrProducts
  );
  if (!hasActiveDiscountValue(discount.discount_value)) {
    // Clear stale fields so overall↔per_product switches / 0% rows don't leak.
    return stripProductDiscountFields(product);
  }
  return { ...product, ...discount };
}

export function applySectionDiscountToResolvedProducts(
  section,
  products,
  offerConfigProducts,
  offerRuleId
) {
  if (!Array.isArray(products)) return products;
  const stampedOfferRuleId =
    offerRuleId != null && String(offerRuleId) !== ""
      ? String(offerRuleId)
      : null;
  const isRuleScoped =
    Boolean(stampedOfferRuleId) && stampedOfferRuleId !== "default";

  // Matched Offer Rule: use only that rule's product discounts.
  // Default: schema products win over products_to_offer (editor writes there).
  const configById = isRuleScoped
    ? buildOfferDiscountConfigByProductId(offerConfigProducts)
    : buildOfferDiscountConfigByProductId(
        offerConfigProducts,
        section?.products
      );

  return products.map((product) => {
    const merged = mergeDiscountOntoProduct(section, product, configById);
    if (!stampedOfferRuleId || !merged || typeof merged !== "object") {
      return merged;
    }
    return { ...merged, __weupsellOfferRuleId: stampedOfferRuleId };
  });
}

export function sectionHasAnyActiveDiscount(section, offerConfigProducts) {
  if (hasActiveSectionDiscount(section)) return true;
  const products = [
    ...(Array.isArray(section?.products) ? section.products : []),
    ...(Array.isArray(offerConfigProducts) ? offerConfigProducts : []),
  ];
  return products.some((product) => hasActiveProductDiscount(product));
}

export function buildDiscountRuleFields(source) {
  const maxQuantity = getDiscountQuantityLimit(source);
  const entry = {
    type: source.discount_type || "percentage",
    value: Number(source.discount_value),
    title: source.discount_title || source.discount_message || "",
    message: source.discount_message || "",
    minQuantity: source.discount_minimum_quantity
      ? Number(source.discount_minimum_quantity)
      : 1,
  };
  if (maxQuantity != null) {
    entry.maxQuantity = maxQuantity;
  }
  return entry;
}
